import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CircleNotch, Printer, Cube, LockSimple } from "@phosphor-icons/react";
import EisenhowerBlueprint from "@/components/blueprints/EisenhowerBlueprint";
import CalibrationBlueprint from "@/components/blueprints/CalibrationBlueprint";
import CoachingBlueprint from "@/components/blueprints/CoachingBlueprint";
import CommunicationBlueprint from "@/components/blueprints/CommunicationBlueprint";
import FocusArmorBlueprint from "@/components/blueprints/FocusArmorBlueprint";
import PlayingToWinBlueprint from "@/components/blueprints/PlayingToWinBlueprint";
import SipocBlueprint from "@/components/blueprints/SipocBlueprint";
import DmaicBlueprint from "@/components/blueprints/DmaicBlueprint";
import TocBlueprint from "@/components/blueprints/TocBlueprint";
import FiveForcesBlueprint from "@/components/blueprints/FiveForcesBlueprint";
import VrioBlueprint from "@/components/blueprints/VrioBlueprint";
import SwotBlueprint from "@/components/blueprints/SwotBlueprint";
import LongPestelBlueprint from "@/components/blueprints/LongPestelBlueprint";
import CccBlueprint from "@/components/blueprints/CccBlueprint";
import FmeaBlueprint from "@/components/blueprints/FmeaBlueprint";
import AssetLifecycleBlueprint from "@/components/blueprints/AssetLifecycleBlueprint";
import CongruenceBlueprint from "@/components/blueprints/CongruenceBlueprint";
import NineBoxBlueprint from "@/components/blueprints/NineBoxBlueprint";
import KeyPersonBlueprint from "@/components/blueprints/KeyPersonBlueprint";
import RaciEBlueprint from "@/components/blueprints/RaciEBlueprint";
import AnsoffBlueprint from "@/components/blueprints/AnsoffBlueprint";

const PILLAR_TAG = {
  STRATEGY: "text-indigo-700",
  OPERATIONS: "text-amber-700",
  PEOPLE: "text-emerald-700",
};

function Blueprint({ doc }) {
  switch (doc.tool_type) {
    case "eisenhower": return <EisenhowerBlueprint doc={doc} />;
    case "calibration": return <CalibrationBlueprint doc={doc} />;
    case "coaching": return <CoachingBlueprint doc={doc} />;
    case "communication_strategy": return <CommunicationBlueprint doc={doc} />;
    case "playing_to_win": return <PlayingToWinBlueprint doc={doc} />;
    case "sipoc": return <SipocBlueprint doc={doc} />;
    case "dmaic": return <DmaicBlueprint doc={doc} />;
    case "toc": return <TocBlueprint doc={doc} />;
    case "five_forces": return <FiveForcesBlueprint doc={doc} />;
    case "vrio": return <VrioBlueprint doc={doc} />;
    case "swot": return <SwotBlueprint doc={doc} />;
    case "long_pestel": return <LongPestelBlueprint doc={doc} />;
    case "ccc": return <CccBlueprint doc={doc} />;
    case "fmea": return <FmeaBlueprint doc={doc} />;
    case "asset_lifecycle": return <AssetLifecycleBlueprint doc={doc} />;
    case "congruence": return <CongruenceBlueprint doc={doc} />;
    case "nine-box-matrix": return <NineBoxBlueprint doc={doc} />;
    case "key-person-risk": return <KeyPersonBlueprint doc={doc} />;
    case "raci-e-grid": return <RaciEBlueprint doc={doc} />;
    case "ansoff": return <AnsoffBlueprint doc={doc} />;
    case "focus-armor": return <FocusArmorBlueprint doc={doc} />;
    default: return <p className="text-slate-400">Unsupported document type.</p>;
  }
}

export default function SharedDocument() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/shared/${token}`);
        setDoc(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading)
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400"><CircleNotch size={32} className="animate-spin" /></div>;

  if (notFound || !doc)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-6" data-testid="shared-not-found">
        <LockSimple size={40} weight="duotone" className="text-slate-300" />
        <h1 className="mt-4 text-2xl font-bold text-slate-900">Link unavailable</h1>
        <p className="mt-2 text-slate-500 max-w-sm">This shared link is invalid or has been revoked by its owner.</p>
      </div>
    );

  const b = doc.branding || {};
  const orgLabel = b.organization_name || "MODUS";

  return (
    <div className="min-h-screen bg-slate-50 font-['IBM_Plex_Sans',sans-serif] print:bg-white" data-testid="shared-document">
      <header className="border-b border-slate-200 bg-white print:hidden">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="shared-brand">
            {b.logo_url ? (
              <img src={b.logo_url} alt="" data-testid="shared-brand-logo" className="h-8 max-w-[150px] object-contain" />
            ) : (
              <Cube size={26} weight="duotone" className="text-indigo-500" />
            )}
            <div className="leading-none">
              <span className="font-mono text-base font-bold tracking-tight text-slate-900">{orgLabel}</span>
              <p className="mt-0.5 text-[10px] tracking-[0.14em] uppercase text-slate-400">Shared · Read-only</p>
            </div>
          </div>
          <button data-testid="shared-print-button" onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Printer size={16} /> Print / PDF
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-8 print:px-0 print:py-0 print:max-w-none">
        {/* Print-visible branded header */}
        {(b.organization_name || b.logo_url) && (
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200" data-testid="shared-print-brand">
            {b.logo_url && <img src={b.logo_url} alt="" className="h-9 max-w-[160px] object-contain" />}
            {b.organization_name && <span className="font-semibold text-slate-900 tracking-tight">{b.organization_name}</span>}
          </div>
        )}
        <p className={`text-xs font-semibold tracking-[0.12em] uppercase ${PILLAR_TAG[doc.pillar] || "text-slate-500"}`}>{doc.tool_name}</p>
        <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{doc.title}</h1>
        <div className="mt-6"><Blueprint doc={doc} /></div>
        <div className="mt-10 pt-4 border-t border-slate-200 font-mono text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <span>{b.organization_name ? `${b.organization_name} · ` : ""}Shared via MODUS</span>
          <span className="text-slate-400">{b.custom_footer_text || "Read-only copy"}</span>
        </div>
      </main>
    </div>
  );
}
